<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { DgrLoading } from '@stockmarkteam/donguri-ui';
import SearchBar from '@/components/common/molecules/search-bar.vue';
import TextTabItem from '@/components/common/molecules/text-tab-item.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { useOrganizationTagList, useUserInfo, useUsers } from '@/utils/swr';

type TabName = 'organization_tag' | 'user';

export interface FollowListSelection {
  id: number;
  type: TabName;
}

interface Props {
  disabled: boolean;
  largeTab?: boolean;
}

const props = withDefaults(defineProps<Props>(), { largeTab: false });

const emits = defineEmits<{
  select: [value: FollowListSelection | null];
  loading: [value: boolean];
}>();

const { createSnackbar } = useSnackbar();

// get 関連の処理
const { data: organizationTagList, state: organizationTagState } =
  useOrganizationTagList();
const { data: userInfo, state: userInfoState } = useUserInfo();
const { data: rawUsers, state: usersState } = useUsers();
// NOTE: API response が users.users みたいな形でわかりにくいため整形している
// このAPI自体は他の箇所でも使用されているので、API側の修正はしていない
const users = computed(() => rawUsers.value?.users);

watch([organizationTagState, userInfoState], () => {
  if (
    organizationTagState.value === 'ERROR' ||
    userInfoState.value === 'ERROR'
  ) {
    createSnackbar({
      message: '組織タグの取得に失敗しました',
      type: 'error',
    });
  }
});
watch([usersState], () => {
  if (usersState.value === 'ERROR') {
    createSnackbar({
      message: 'メンバーの取得に失敗しました',
      type: 'error',
    });
  }
});
const belongingData = computed(() => {
  return organizationTagList.value?.organization_tags?.filter(item =>
    userInfo.value?.organization_tags.includes(item.name),
  );
});
const unBelongingData = computed(() => {
  return organizationTagList.value?.organization_tags?.filter(
    item => belongingData.value?.indexOf(item) === -1,
  );
});

const visibleTab = ref<TabName>('organization_tag');
const changeVisible = (visibleState: TabName): void => {
  if (visibleState === visibleTab.value) return;
  visibleTab.value = visibleState;
};

// 選択機能
const selectedOrganizationTags = ref<number[]>([]);
const onSelectOrganizationTag = (id: number) => {
  if (props.disabled) return;

  selectedOrganizationTags.value = [id];
  updateSelection();
};
const selectedUsers = ref<number[]>([]);
const onSelectUser = (id: number) => {
  if (props.disabled) return;

  selectedUsers.value = [id];
  updateSelection();
};

// state
const isLoadingForOrganizationTags = computed(() => {
  return (
    organizationTagState.value === 'PENDING' ||
    userInfoState.value === 'PENDING' ||
    usersState.value === 'PENDING'
  );
});
const isLoadingForUsers = computed(() => {
  return (
    organizationTagState.value === 'PENDING' ||
    userInfoState.value === 'PENDING' ||
    usersState.value === 'PENDING'
  );
});

// メンバー検索機能
const filterQuery = ref('');
const filteredUsers = computed(() => {
  if (!users.value) return [];
  if (!filterQuery.value) return users.value;

  return users.value.filter(user => {
    const isIncludeUserName = () =>
      user.user_name.toLowerCase().includes(filterQuery.value.toLowerCase());
    const isIncludeEmail = () =>
      user.email.toLowerCase().includes(filterQuery.value.toLowerCase());

    return isIncludeUserName() || isIncludeEmail();
  });
});
const sortedUsers = computed(() =>
  [...filteredUsers.value].sort((a, b) => {
    return a.user_name.localeCompare(b.user_name);
  }),
);
watch(visibleTab, () => {
  filterQuery.value = '';
  updateSelection();
});

// 選択状態の更新と emit
const updateSelection = () => {
  let selection: FollowListSelection | null = null;
  let hasSelection = false;

  if (visibleTab.value === 'organization_tag') {
    hasSelection = selectedOrganizationTags.value.length > 0;
    if (hasSelection) {
      selection = {
        id: selectedOrganizationTags.value[0],
        type: 'organization_tag',
      };
    }
  } else {
    hasSelection = selectedUsers.value.length > 0;
    if (hasSelection) {
      selection = {
        id: selectedUsers.value[0],
        type: 'user',
      };
    }
  }

  emits('select', selection);
};

// ローディング状態の emit
const isLoading = computed(() => {
  if (visibleTab.value === 'organization_tag') {
    return isLoadingForOrganizationTags.value;
  }
  return isLoadingForUsers.value;
});

watch(
  isLoading,
  value => {
    emits('loading', value);
  },
  { immediate: true },
);
</script>

<template>
  <div class="create-follow-list-form" :class="{ narrow: largeTab }">
    <div class="tab-wrapper" :class="{ large: largeTab }">
      <button class="button" @click="changeVisible('organization_tag')">
        <TextTabItem
          class="tab-item"
          text="組織タグ"
          :is-selected="visibleTab === 'organization_tag'"
          :disabled="props.disabled"
        />
      </button>
      <button class="button" @click="changeVisible('user')">
        <TextTabItem
          class="tab-item"
          text="メンバー"
          :is-selected="visibleTab === 'user'"
          :disabled="props.disabled"
        />
      </button>
    </div>
    <template v-if="visibleTab === 'organization_tag'">
      <div v-if="isLoadingForOrganizationTags" class="loading">
        <DgrLoading />
      </div>
      <div
        v-if="organizationTagList?.organization_tags.length === 0"
        class="message-wrapper"
      >
        <div class="empty-message">
          <div class="c-title-bold--s">組織タグは未作成です。</div>
          <div class="c-text-regular--s">
            組織タグを作成するには、管理者権限が必要です。
          </div>
        </div>
      </div>
      <div v-else class="organization-tag-contents">
        <div
          v-if="(belongingData?.length ?? 0) > 0"
          class="organization-tag-content"
        >
          <p class="sub-title">所属中</p>
          <button
            v-for="item in belongingData"
            :key="item.id"
            @click="onSelectOrganizationTag(item.id)"
            :disabled="props.disabled"
            :aria-selected="selectedOrganizationTags.includes(item.id)"
            :aria-disabled="props.disabled"
            class="list-item button"
          >
            {{ item.name }}
          </button>
        </div>
        <div
          v-if="(unBelongingData?.length ?? 0) > 0"
          class="organization-tag-content"
        >
          <p class="sub-title">未所属</p>
          <button
            v-for="item in unBelongingData"
            :key="item.id"
            @click="onSelectOrganizationTag(item.id)"
            :disabled="props.disabled"
            :aria-selected="selectedOrganizationTags.includes(item.id)"
            :aria-disabled="props.disabled"
            class="list-item button"
          >
            {{ item.name }}
          </button>
        </div>
      </div>
    </template>
    <template v-else>
      <div v-if="isLoadingForUsers" class="loading">
        <DgrLoading />
      </div>
      <div class="user-contents">
        <template v-if="!isLoadingForUsers">
          <SearchBar
            v-model="filterQuery"
            placeholder="検索"
            class="search-bar"
          />
          <div class="user-list">
            <div class="user-list-header">
              <p class="sub-title table-title">名前</p>
              <p class="sub-title">マークの公開範囲</p>
            </div>
            <button
              v-for="user in sortedUsers"
              :key="user.id"
              :disabled="props.disabled"
              :aria-selected="selectedUsers.includes(user.id)"
              :aria-disabled="props.disabled"
              @click="onSelectUser(user.id)"
              class="list-item button user-list-item"
            >
              <div class="name-column">
                <p>{{ user.user_name }}</p>
                <p class="email c-text-regular--xs">{{ user.email }}</p>
              </div>
              <div class="scope-column">
                {{
                  user.mypage_access_scope === 'public' ? '全体' : '自分のみ'
                }}
              </div>
            </button>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.create-follow-list-form {
  display: flex;
  flex-direction: column;
}
.loading {
  padding: 16px;
}
.tab-wrapper {
  display: flex;
  margin-top: -12px;
  border-bottom: 1px solid $color-gray400;
  // NOTE: 本来はclassを渡して対応したいですが、tab text itemの内部実装の影響で強めの指定をしています
  &:not(.large) * {
    padding: 0;
    width: 120px;
  }

  &.large {
    justify-content: stretch;
    button {
      flex-grow: 1;
    }
    .tab-item {
      width: 100%;
    }
  }
}
.organization-tag-contents {
  overflow-y: auto;
}
.organization-tag-content {
  padding: 20px 0 4px 0;
  &:not(:last-of-type) {
    border-bottom: 1px solid $color-gray400;
  }
}
.sub-title {
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  padding: 5px 0;
}
.user-contents {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.user-list {
  padding: 20px 0 4px 0;
  overflow-y: auto;
  flex-grow: 1;
}
.user-list-header {
  display: flex;
}
.search-bar {
  padding: 20px 0 0;
  box-sizing: border-box;
}
.button {
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  appearance: none;
  height: auto;
}
.list-item {
  padding: 12px 8px;
  font-size: 14px;
  line-height: 22px;
  border-radius: 0;
  border-bottom: 1px solid $color-gray400;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  &:last-of-type {
    border-bottom: none;
  }
  &:first-of-type {
    border-top: 1px solid $color-gray400 !important;
  }
  &:hover {
    background-color: $color-gray200;
  }
  &[aria-selected='true'] {
    background-color: $color-green100;
    color: $color-green600;
  }
  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
}
.user-list-item {
  display: flex;
}
.table-title {
  width: 580px;
}
.name-column {
  width: 600px;
  * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
.email {
  color: $color-text-secondary;
}
.scope-column {
  width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.message-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}
.empty-message {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 16px;
  gap: 12px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
}

.narrow {
  .organization-tag-contents,
  .search-bar,
  .user-list {
    margin: 0 16px;
  }
  .search-bar {
    width: calc(100% - 32px);
  }
}
</style>
